import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, UserCheck } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { HandoverPanel } from "../components/HandoverPanel";
import { useHandoverQueue, type HandoverQueueRow } from "../api/handover";

interface PickerUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_active: boolean;
}

/** Company-wide picker: przedsięwzięcie odchodzącego bywa przejmowane przez kogoś
 *  z innego oddziału, więc lista następców celowo nie jest zawężana oddziałem. */
function useSelectableUsers() {
  return useQuery({
    queryKey: ["users", "selectable", "handover"],
    queryFn: async () => {
      const resp = await apiClient.get<{ data: PickerUser[] }>(
        "/auth/users/?selectable=true",
      );
      return resp.data.data;
    },
  });
}

function fullName(u: PickerUser) {
  return `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email;
}

function whenLabel(row: HandoverQueueRow) {
  if (!row.is_active) {
    const d = row.deactivated_at
      ? new Date(row.deactivated_at).toLocaleDateString("pl-PL")
      : "—";
    return { text: `konto wyłączone ${d}`, color: "red" as const };
  }
  if (row.days_left === null) return { text: "odchodzi", color: "yellow" as const };
  if (row.days_left <= 0) return { text: "termin minął", color: "red" as const };
  return {
    text: `odchodzi za ${row.days_left} dni`,
    color: row.days_left <= 7 ? ("orange" as const) : ("yellow" as const),
  };
}

/**
 * Przekazanie obowiązków — osobny ekran, nie zakładka na karcie pracownika.
 *
 * Zaczyna od kolejki: kto odchodzi albo już odszedł i nadal coś prowadzi. Karta
 * pracownika odpowiada na pytanie "kim jest ta osoba"; tutaj odpowiada się na
 * "co jeszcze wisi i kto to przejmie" — inny moment i inna decyzja.
 */
export function HandoverPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("employee");
  const { data: queue, isLoading: queueLoading } = useHandoverQueue();
  const { data: users } = useSelectableUsers();
  const [adHoc, setAdHoc] = useState("");

  const options = useMemo(
    () =>
      (users ?? [])
        .map((u) => ({ value: u.id, label: fullName(u) }))
        .sort((a, b) => a.label.localeCompare(b.label, "pl")),
    [users],
  );

  const selectedName = useMemo(() => {
    const fromQueue = queue?.find((r) => r.id === selected)?.name;
    if (fromQueue) return fromQueue;
    const u = users?.find((x) => x.id === selected);
    return u ? fullName(u) : "";
  }, [queue, users, selected]);

  const open = (id: string) => {
    params.set("employee", id);
    setParams(params);
  };

  return (
    <div className="p-4 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Przekazanie obowiązków
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Jedno przedsięwzięcie przechodzi na jedną osobę razem ze wszystkim, co
          odchodzący w nim trzyma. Pełnomocnictwa się nie przepinają — trzeba je
          wystawić na nowo.
        </p>
      </div>

      {/* — Kolejka: kto wymaga rozliczenia — */}
      <section>
        <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Do rozliczenia
        </h3>
        {queueLoading ? (
          <Spinner />
        ) : !queue?.length ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Nikt odchodzący ani wyłączony nie ma otwartych spraw. Czysto.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="px-2 py-2">Osoba</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2 text-right">Pozycji</th>
                  <th className="px-2 py-2 text-right">Pełnomocnictw</th>
                  <th className="px-2 py-2 text-right">Dostępów</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {queue.map((row) => {
                  const when = whenLabel(row);
                  return (
                    <tr
                      key={row.id}
                      className={selected === row.id ? "bg-red-50/50 dark:bg-red-900/10" : ""}
                    >
                      <td className="px-2 py-2 font-medium">{row.name}</td>
                      <td className="px-2 py-2">
                        <Badge color={when.color}>{when.text}</Badge>
                      </td>
                      <td className="px-2 py-2 text-right">
                        {row.totals.transferable}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {row.totals.poa > 0 ? (
                          <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {row.totals.poa}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {row.totals.accesses || "—"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Button size="sm" onClick={() => open(row.id)}>
                          Rozlicz
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* — Dowolna osoba: urlop, przeciążenie, zmiana oddziału — */}
      <section className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Inna osoba
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Ten sam ekran działa poza odejściami — przy urlopie, przeciążeniu albo
          zmianie oddziału.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[260px]">
            <SearchableSelect
              options={options}
              placeholder="wybierz osobę…"
              value={adHoc}
              onChange={(e) => setAdHoc(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={!adHoc}
            onClick={() => open(adHoc)}
          >
            <UserCheck className="h-4 w-4 mr-1" />
            Pokaż, co prowadzi
          </Button>
        </div>
      </section>

      {/* — Rozliczenie wybranej osoby — */}
      {selected && (
        <section className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <HandoverPanel
            key={selected}
            userId={selected}
            userName={selectedName}
            candidates={users ?? []}
          />
        </section>
      )}
    </div>
  );
}
